/**
 * Field length limits for profile records (business_profiles, admin_profiles).
 *
 * The drizzle schema imports these as `varchar(...)` length args; backend
 * validators + frontend zod schemas import them for the same checks.
 * Change here → next `pnpm db:generate` emits an ALTER COLUMN migration.
 */
export const PROFILE_NAME_MAX_LENGTH = 100

/** An optional nickname others see in place of the name. */
export const PROFILE_NICKNAME_MAX_LENGTH = 30

/** A status line, shown to friends for 24 hours. */
export const PROFILE_STATUS_MAX_LENGTH = 60
export const PROFILE_STATUS_TTL_MS = 24 * 60 * 60 * 1000

/** Handle / @username rules. Stored lowercased; a-z, 0-9, underscore only. */
export const USERNAME_MIN_LENGTH = 3
export const USERNAME_MAX_LENGTH = 20
export const USERNAME_REGEX = /^[a-z0-9_]+$/

/** How often someone may replace a handle once it is set. */
export const USERNAME_MAX_CHANGES = 1

/** Whether a set handle may still be replaced; picking the first one is always allowed. */
export function canChangeUsername(profile: {
  username: string | null
  usernameChanges: number
}): boolean {
  return profile.username == null || profile.usernameChanges < USERNAME_MAX_CHANGES
}

/** Lowercase + trim; the canonical form stored and compared for uniqueness. */
export function normalizeUsername(input: string): string {
  return input.trim().toLowerCase()
}

export function isValidUsername(input: string): boolean {
  const u = normalizeUsername(input)
  return (
    u.length >= USERNAME_MIN_LENGTH && u.length <= USERNAME_MAX_LENGTH && USERNAME_REGEX.test(u)
  )
}

/** The "Über mich" line on a profile. Counted in characters the way Postgres `varchar` does. */
export const PROFILE_BIO_MAX_LENGTH = 150

/** Photos a person can show besides the avatar. */
export const PROFILE_PHOTOS_MAX = 5

/** Characters as Postgres counts them (code points), so emoji count once. */
export function characterCount(text: string): number {
  return [...text].length
}

/** Trimmed bio, null when empty; `undefined` when it is longer than PROFILE_BIO_MAX_LENGTH. */
export function normalizeBio(raw: string | null): string | null | undefined {
  if (raw === null) return null
  const text = raw.replace(/\r\n?/g, '\n').trim()
  if (text.length === 0) return null
  return characterCount(text) > PROFILE_BIO_MAX_LENGTH ? undefined : text
}

/** Colours a person can pick for the ring around their profile picture; null means automatic. */
export const PROFILE_RING_COLORS = [
  'coral',
  'amber',
  'rose',
  'violet',
  'sky',
  'mint',
  'forest',
  'graphite',
] as const

export type ProfileRingColor = (typeof PROFILE_RING_COLORS)[number]
